import { DomainDivider } from "./DomainDivider";
import { LiteBox } from "../../../../../components/lite/LiteBox";
import { Button, Link, Typography } from "@material-ui/core";
import useOrganizationStyles from "../../use-organization-styles";
import { GetOrganizationDomainsWithCountersQuery } from "../../types/queries.generated";
import { useTranslation } from "react-i18next";
import { OpenInNew } from "@material-ui/icons";
import { DotIcon } from "../../../../../components/icons/DotIcon";
import { DomainTable } from "./DomainTable";
import { useHistory } from "react-router";
import React from "react";
import ProductDomainDivider from "./ProductDomainDivider";
import { LyfeTooltip } from "@lyfegen-platform/react-common";
import { sortByField } from "@lyfegen-platform/utils";
interface IDomainCardProps {
	globalDomain?: GetOrganizationDomainsWithCountersQuery["GetOrganizationDomainsWithCounters"]["domains"][0];
	userDomains: GetOrganizationDomainsWithCountersQuery["GetOrganizationDomainsWithCounters"]["domains"];
}
export type DomainType = Omit<GetOrganizationDomainsWithCountersQuery["GetOrganizationDomainsWithCounters"]["domains"][0], "children"> & {
	children?: DomainType[];
};
export const MAX_SUBDOMAINS_LIMIT = 50;
export const MAX_DOMAINS_LIMIT = 10;

export const sortDomainsByName = (domains: DomainType[]): DomainType[] => {
	if (!domains) {
		return [];
	}

	return domains
		.map((domain) => ({
			...domain,
			children: domain.children ? sortDomainsByName(domain.children) : [],
		}))
		.sort(sortByField("name"));
};

export const DomainCard = (props: IDomainCardProps) => {
	const { t } = useTranslation();
	const { push } = useHistory();
	const { newTabLink, newTabLinkIcon } = useOrganizationStyles();
	const globalDomain = props.globalDomain;
	const productDomains = globalDomain ? globalDomain?.children : props.userDomains;
	const sortedProductDomains = sortDomainsByName(productDomains as DomainType[]);

	const handleUserClick = (e: React.SyntheticEvent, domainId: string) => {
		e.preventDefault();
		const win = window.open(`/organization-settings`, "_blank") as Window;
		win.sessionStorage.setItem("domain", domainId);
		win.focus();
	};

	const handleSimulationClick = (e: React.SyntheticEvent, domainId: string) => {
		e.preventDefault();
		const win = window.open(`/simulator`, "_blank") as Window;
		const domainName =
			globalDomain?.id === domainId
				? globalDomain.name || ""
				: productDomains.find((domain) => domain.id === domainId)?.name ||
				  productDomains.flatMap((domain) => domain.children || []).find((child) => child.id === domainId)?.name ||
				  "";

		win.sessionStorage.setItem("domain", domainName);
		win.focus();
	};

	const handleCreateNewProductDomainClick = (e: React.SyntheticEvent) => {
		e.preventDefault();
		push(`/organization-settings/domains/${globalDomain?.id}/create-domain`);
	};

	const handleCreateNewSubdomainClick = (e: React.SyntheticEvent, parentDomainId: string) => {
		e.preventDefault();
		push(`/organization-settings/domains/${parentDomainId}/create-domain`);
	};

	// Add level 2 domain
	const AddSubdomainButton = ({ parentDomainId }: { parentDomainId: string }) => {
		const isDisabled = (productDomains.find((domain) => domain.id === parentDomainId)?.children?.length ?? 0) >= MAX_SUBDOMAINS_LIMIT;

		return (
			<LyfeTooltip title={isDisabled ? t("product-subdomain-max-limit") : ""}>
				<span>
					<Button
						variant="outlined"
						color="primary"
						onClick={(e) => handleCreateNewSubdomainClick(e, parentDomainId)}
						disabled={isDisabled}
					>
						{t("new-product-subdomain")}
					</Button>
				</span>
			</LyfeTooltip>
		);
	};

	// Add level 1 domain
	const addProductDomainButton = () => {
		const isDisabled = productDomains.length >= MAX_DOMAINS_LIMIT;

		return (
			<LyfeTooltip title={isDisabled ? t("product-domain-max-limit") : ""}>
				<span>
					<Button variant="contained" color="primary" onClick={handleCreateNewProductDomainClick} disabled={isDisabled}>
						{t("new-product-domain")}
					</Button>
				</span>
			</LyfeTooltip>
		);
	};

	/* GLOBAL DOMAIN ACCESS (LEVEL 0) */
	const globalDomainSectionCounter = (
		<>
			{globalDomain && (
				<LiteBox width="100%" display="flex" mb={3} pt={1} pl={1}>
					<Typography variant="subtitle2" color="textPrimary">
						{productDomains.length} {t("simulator-product-domain-counter").toLowerCase()}
					</Typography>
					<DotIcon />
					<Link className={newTabLink} onClick={(event) => handleUserClick(event, globalDomain?.id || "")} underline="none">
						<Typography variant="subtitle2" color="primary">
							{`${globalDomain?.users.length} ${t("organization-settings-tab-users").toLowerCase()}`}
						</Typography>
						<OpenInNew color="primary" className={newTabLinkIcon} />
					</Link>
					<DotIcon />
					<Link className={newTabLink} onClick={(event) => handleSimulationClick(event, globalDomain?.id || "")} underline="none">
						<Typography variant="subtitle2" color="primary">
							{`${globalDomain?.simulationCount} ${t("domain-table-simulations").toLowerCase()}`}
						</Typography>
						<OpenInNew color="primary" className={newTabLinkIcon} />
					</Link>
				</LiteBox>
			)}
		</>
	);
	const content = (
		<LiteBox>
			{/* GLOBAL AND PRODUCT DOMAIN ACCESS (LEVEL 0,1) */}
			{sortedProductDomains.length > 0 && (
				<>
					{sortedProductDomains.map((productDomain) => (
						<ProductDomainDivider
							key={productDomain.id}
							title={productDomain.name}
							AddSubdomainButtonComponent={AddSubdomainButton}
							id={productDomain.id}
							counterSection={
								<LiteBox paddingTop={1} pl={1} width="100%" display="flex" alignItems={"center"}>
									<Typography variant="subtitle2" color="textPrimary">
										{productDomain?.children?.length} {t("simulator-product-subdomain-counter").toLowerCase()}
									</Typography>
									<DotIcon />
									{!!productDomain?.users.length ? (
										<Link
											className={newTabLink}
											onClick={(event) => handleUserClick(event, productDomain?.id || "")}
											underline="none"
										>
											<Typography variant="subtitle2" color="primary">
												{`${productDomain?.users.length} ${t("organization-settings-tab-users").toLowerCase()}`}
											</Typography>
											<OpenInNew color="primary" className={newTabLinkIcon} />
										</Link>
									) : (
										<Typography variant="subtitle2" color="textPrimary">
											{`${productDomain?.users.length} ${t("organization-settings-tab-users").toLowerCase()}`}
										</Typography>
									)}
									<DotIcon />
									{!!productDomain?.simulationCount ? (
										<Link
											className={newTabLink}
											onClick={(event) => handleSimulationClick(event, productDomain?.id || "")}
											underline="none"
										>
											<Typography variant="subtitle2" color="primary">
												{`${productDomain?.simulationCount} ${t("domain-table-simulations").toLowerCase()}`}
											</Typography>
											<OpenInNew color="primary" className={newTabLinkIcon} />
										</Link>
									) : (
										<Typography variant="subtitle2" color="textPrimary">
											{`${productDomain?.simulationCount} ${t("domain-table-simulations").toLowerCase()}`}
										</Typography>
									)}
								</LiteBox>
							}
							content={
								<LiteBox pl={1}>
									{productDomain?.children?.length ? (
										<DomainTable
											domains={productDomain.children}
											handleUserClick={handleUserClick}
											handleSimulationClick={handleSimulationClick}
											domainColumnKey={"simulator-product-subdomains"}
										/>
									) : (
										<></>
									)}
								</LiteBox>
							}
						/>
					))}
				</>
			)}
		</LiteBox>
	);

	return (
		<LiteBox>
			<DomainDivider
				title={globalDomain?.name || ""}
				counterSection={globalDomainSectionCounter}
				addProductDomainButton={addProductDomainButton}
				content={content}
				globalDomainId={globalDomain?.id}
			/>
		</LiteBox>
	);
};
